import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { useSmsFeature } from '../../contexts/FeatureFlagContext';
import { CampaignLoadingSkeleton } from '../../components/Campaigns/CampaignLoadingSkeleton';
import { CampaignEmptyState } from '../../components/Campaigns/CampaignEmptyState';
import type { Campaign } from '../../types/campaign';

export const CampaignsListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isSmsEnabled = useSmsFeature();
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [channelFilter, setChannelFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(25);
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Fetch campaigns
  const { data: campaigns = [], isLoading, error } = useQuery<Campaign[]>({
    queryKey: ['campaigns', statusFilter, channelFilter],
    queryFn: async () => {
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      if (channelFilter) params.channel = channelFilter;
      const response = await api.campaigns.list(params);
      const data = response.data;
      // Ensure we always return an array
      return Array.isArray(data) ? data : [];
    },
  });

  // Filter campaigns by search query
  const filteredCampaigns = campaigns.filter((campaign) =>
    campaign.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Paginate filtered campaigns
  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedCampaigns = filteredCampaigns.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredCampaigns.length / pageSize);
  const hasMore = endIndex < filteredCampaigns.length;
  
  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(0);
  }, [statusFilter, channelFilter, searchQuery]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.campaigns.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campaign deleted successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete campaign: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Start/Enqueue mutation
  const enqueueMutation = useMutation({
    mutationFn: (id: string) => api.campaigns.enqueue(id, { dry_run: false }),
    onMutate: async (campaignId: string) => {
      // Optimistically update campaign status in the list
      await queryClient.cancelQueries({ queryKey: ['campaigns'] });
      
      const previousCampaigns = queryClient.getQueryData<Campaign[]>(['campaigns', statusFilter, channelFilter]);
      
      if (previousCampaigns) {
        queryClient.setQueryData<Campaign[]>(['campaigns', statusFilter, channelFilter], (old) => {
          if (!old) return old;
          return old.map(campaign => 
            campaign.id === campaignId 
              ? { ...campaign, status: 'scheduled' as const }
              : campaign
          );
        });
      }
      
      return { previousCampaigns };
    },
    onSuccess: async (response: any, campaignId: string) => {
      // Show detailed results
      const data = response.data;
      if (data.queued_contacts && data.queued_contacts > 0) {
        toast.success(`Campaign started - ${data.queued_contacts} contact(s) queued`);
      } else {
        toast.warning(`Campaign started but no contacts were queued`);
      }
      
      // Show warnings about skipped contacts
      if (data.skipped_contacts && data.skipped_contacts > 0) {
        toast.info(`${data.skipped_contacts} contact(s) skipped`);
      }
      
      // Show errors if any
      if (data.errors && data.errors.length > 0) {
        data.errors.forEach((error: string) => {
          toast.error(error);
        });
      }
      
      // Immediately refetch campaigns list to get updated status and metrics
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['campaigns'] }),
        queryClient.refetchQueries({ queryKey: ['campaign', campaignId] }),
        queryClient.refetchQueries({ queryKey: ['campaign-metrics', campaignId] }),
      ]);
    },
    onError: (error: any, campaignId: string, context) => {
      // Rollback optimistic update on error
      if (context?.previousCampaigns) {
        queryClient.setQueryData<Campaign[]>(['campaigns', statusFilter, channelFilter], context.previousCampaigns);
      }
      toast.error(`Failed to start campaign: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Pause mutation
  const pauseMutation = useMutation({
    mutationFn: (id: string) => api.campaigns.pause(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campaign paused successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to pause campaign: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Resume mutation
  const resumeMutation = useMutation({
    mutationFn: (id: string) => api.campaigns.resume(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campaign resumed successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to resume campaign: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: (id: string) => api.campaigns.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campaign cancelled successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to cancel campaign: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Action handlers
  const handleView = (id: string) => {
    navigate(`/campaigns/${id}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/campaigns/${id}/edit`);
  };

  const handleDelete = (campaign: Campaign) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Campaign',
      message: `Are you sure you want to delete "${campaign.name}"? This action cannot be undone.`,
      onConfirm: () => {
        deleteMutation.mutate(campaign.id);
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      },
    });
  };

  const handleStart = (campaign: Campaign) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Start Campaign',
      message: `Are you sure you want to start "${campaign.name}"? This will send messages to ${campaign.total_contacts || 0} contacts.`,
      onConfirm: () => {
        enqueueMutation.mutate(campaign.id);
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      },
    });
  };

  const handlePause = (id: string) => {
    pauseMutation.mutate(id);
  };

  const handleResume = (id: string) => {
    resumeMutation.mutate(id);
  };

  const handleCancel = (campaign: Campaign) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Cancel Campaign',
      message: `Are you sure you want to cancel "${campaign.name}"? This will stop all pending messages.`,
      onConfirm: () => {
        cancelMutation.mutate(campaign.id);
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      },
    });
  };

  // Status badge component
  const StatusBadge: React.FC<{ status: Campaign['status'] }> = ({ status }) => {
    const statusStyles = {
      draft: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      running: 'bg-green-100 text-green-800 animate-pulse',
      paused: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Channel badge component
  const ChannelBadge: React.FC<{ channel: Campaign['channel'] }> = ({ channel }) => {
    const channelStyles = {
      email: 'bg-purple-100 text-purple-800',
      sms: 'bg-indigo-100 text-indigo-800',
      both: 'bg-pink-100 text-pink-800',
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${channelStyles[channel]}`}>
        {channel.toUpperCase()}
      </span>
    );
  };

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Calculate percentage
  const calculatePercentage = (part?: number, total?: number) => {
    if (!part || !total || total === 0) return '0%';
    return `${Math.round((part / total) * 100)}%`;
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Campaigns</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage your marketing campaigns and track their performance
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            type="button"
            onClick={() => navigate('/campaigns/new')}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Create Campaign
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {/* Search */}
        <div>
          <label htmlFor="search" className="block text-sm font-medium text-gray-700">
            Search
          </label>
          <input
            type="text"
            id="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name..."
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>

        {/* Status Filter */}
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700">
            Status
          </label>
          <select
            id="status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="running">Running</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Channel Filter */}
        <div>
          <label htmlFor="channel" className="block text-sm font-medium text-gray-700">
            Channel
          </label>
          <select
            id="channel"
            value={channelFilter}
            onChange={(e) => setChannelFilter(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="">All Channels</option>
            <option value="email">Email</option>
            {isSmsEnabled && <option value="sms">SMS</option>}
            {isSmsEnabled && <option value="both">Both</option>}
          </select>
        </div>
      </div>

      {/* Clear Filters */}
      {(statusFilter || channelFilter || searchQuery) && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => {
              setStatusFilter('');
              setChannelFilter('');
              setSearchQuery('');
            }}
            className="text-sm text-indigo-600 hover:text-indigo-500"
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && <CampaignLoadingSkeleton type="table" rows={5} />}

      {/* Error State */}
      {error && (
        <div className="mt-8 rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading campaigns</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{(error as any).message || 'An unexpected error occurred'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State - No Campaigns */}
      {!isLoading && !error && campaigns.length === 0 && !statusFilter && !channelFilter && !searchQuery && (
        <CampaignEmptyState 
          type="no-campaigns"
          onAction={() => navigate('/campaigns/new')}
          actionLabel="Create Campaign"
        />
      )}

      {/* Empty State - No Results */}
      {!isLoading && !error && filteredCampaigns.length === 0 && (statusFilter || channelFilter || searchQuery) && (
        <CampaignEmptyState 
          type="no-results"
          onClearFilters={() => {
            setStatusFilter('');
            setChannelFilter('');
            setSearchQuery('');
          }}
        />
      )}

      {/* Campaigns Table */}
      {!isLoading && !error && filteredCampaigns.length > 0 && (
        <div className="mt-8 flex flex-col">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Name
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Channel
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Contacts
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Sent
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Delivered
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Opened
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Created
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {paginatedCampaigns.map((campaign) => (
                      <tr key={campaign.id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                          <div className="font-medium text-gray-900">{campaign.name}</div>
                          {campaign.description && (
                            <div className="text-gray-500 truncate max-w-xs">{campaign.description}</div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <StatusBadge status={campaign.status} />
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <ChannelBadge channel={campaign.channel} />
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {campaign.total_contacts || 0}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {campaign.sent_count || 0}
                          <span className="ml-1 text-xs text-gray-400">
                            ({calculatePercentage(campaign.sent_count, campaign.total_contacts)})
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {campaign.delivered_count || 0}
                          <span className="ml-1 text-xs text-gray-400">
                            ({calculatePercentage(campaign.delivered_count, campaign.sent_count)})
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {campaign.opened_count || 0}
                          <span className="ml-1 text-xs text-gray-400">
                            ({calculatePercentage(campaign.opened_count, campaign.sent_count)})
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {formatDate(campaign.created_at)}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <div className="flex items-center justify-end space-x-2">
                            {/* View Button */}
                            <button
                              onClick={() => handleView(campaign.id)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="View Details"
                            >
                              <EyeIcon className="h-5 w-5" />
                            </button>

                            {/* Edit Button - Only for draft */}
                            {campaign.status === 'draft' && (
                              <button
                                onClick={() => handleEdit(campaign.id)}
                                className="text-gray-600 hover:text-gray-900"
                                title="Edit"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                            )}

                            {/* Start Button - For draft/scheduled */}
                            {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
                              <button
                                onClick={() => handleStart(campaign)}
                                disabled={enqueueMutation.isPending}
                                className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                title="Start Campaign"
                              >
                                <PlayIcon className="h-5 w-5" />
                              </button>
                            )}

                            {/* Pause Button - For running */}
                            {campaign.status === 'running' && (
                              <button
                                onClick={() => handlePause(campaign.id)}
                                disabled={pauseMutation.isPending}
                                className="text-yellow-600 hover:text-yellow-900 disabled:opacity-50"
                                title="Pause Campaign"
                              >
                                <PauseIcon className="h-5 w-5" />
                              </button>
                            )}

                            {/* Resume Button - For paused */}
                            {campaign.status === 'paused' && (
                              <button
                                onClick={() => handleResume(campaign.id)}
                                disabled={resumeMutation.isPending}
                                className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                title="Resume Campaign"
                              >
                                <ArrowPathIcon className="h-5 w-5" />
                              </button>
                            )}

                            {/* Cancel Button - For running/paused */}
                            {(campaign.status === 'running' || campaign.status === 'paused') && (
                              <button
                                onClick={() => handleCancel(campaign)}
                                disabled={cancelMutation.isPending}
                                className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                title="Cancel Campaign"
                              >
                                <StopIcon className="h-5 w-5" />
                              </button>
                            )}

                            {/* Delete Button - For draft/completed/cancelled */}
                            {(campaign.status === 'draft' || campaign.status === 'completed' || campaign.status === 'cancelled') && (
                              <button
                                onClick={() => handleDelete(campaign)}
                                disabled={deleteMutation.isPending}
                                className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                title="Delete"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
          {/* Pagination Controls */}
          {filteredCampaigns.length > pageSize && (
            <div className="mt-4 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                  disabled={currentPage === 0}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={!hasMore}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(endIndex, filteredCampaigns.length)}</span> of{' '}
                    <span className="font-medium">{filteredCampaigns.length}</span> campaigns
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                      disabled={currentPage === 0}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                    <div className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300">
                      Page {currentPage + 1} of {totalPages}
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      disabled={!hasMore}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
            <div className="inline-block transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle">
              <div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg font-medium leading-6 text-gray-900" id="modal-title">
                    {confirmDialog.title}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">{confirmDialog.message}</p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  onClick={confirmDialog.onConfirm}
                  className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm"
                >
                  Confirm
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
                  className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

